import { useEffect, useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Link2, X } from 'lucide-react'
import { useScopeStore } from '../../store'
import { getFieldById, getGroupById } from '../../schema'
import { dealsApi, scopesApi, scopeVersionsApi } from '@/api/client'
import { deriveConstraints } from '../../utils/resellerConstraints'
import { useHasResellerParent } from '../ResellerLock'
import { mapDbScopeToStoreState } from '../../utils/mappers'
import { showToast } from '@store/uiStore'
import { useUpsellContext } from '../../hooks/useUpsellContext'

export function AdvancedGeneralSection() {
  const { scope, updateGeneralInfo } = useScopeStore()
  const { generalInfo } = scope
  const disabled = scope.finalized
  const { mapLabel } = useUpsellContext()

  // --- General fields ---
  const corporateStructureGroup = getGroupById('general_corporate_structure')
  const parentChildField = getFieldById('general_parent_child')
  const parentAccountNameField = getFieldById('general_parent_account_name')
  const parentUatIdField = getFieldById('general_parent_uat_id')
  const parentProdIdField = getFieldById('general_parent_production_id')
  const childAccountNameField = getFieldById('general_child_account_name')
  const childUatIdField = getFieldById('general_child_uat_id')
  const childProdIdField = getFieldById('general_child_production_id')

  const resellerAgreementField = getFieldById('general_reseller_agreement')
  const resellerNameField = getFieldById('general_reseller_name')
  const resellerRelationshipTypeField = getFieldById('general_reseller_relationship_type')
  const resellerPredefinedConfigField = getFieldById('general_reseller_predefined_config')

  const billingRequirementsField = getFieldById('general_billing_requirements')

  const testingGroup = getGroupById('general_testing_group')
  const testingRequirementsField = getFieldById('general_testing_requirements')

  const sowVariationGroup = getGroupById('general_sow_variation_group')
  const sowVariationNoteField = getFieldById('general_sow_variation_note')

  const isResellerChild = useHasResellerParent()

  // Auto-migrate old values to new schema values on mount (only run once)
  useEffect(() => {
    const normalizeParentChild = (value: string): string => {
      if (value === 'child') return 'child' // valid value for reseller children
      if (value === 'parent' || value === 'parent_child') return 'parent_child'
      if (value === 'parent_only') return 'parent_only'
      if (value === 'standalone') return 'parent_only'
      return 'unknown'
    }

    const oldValue = generalInfo.parentChild
    const newValue = normalizeParentChild(oldValue)
    if (oldValue !== newValue && !disabled) {
      console.log('Migrating parentChild from', oldValue, 'to', newValue)
      updateGeneralInfo('parentChild', newValue as any)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Ensure parentChild has a valid value for the dropdown
  const parentChildValue = ['unknown', 'parent_only', 'parent_child', 'child'].includes(generalInfo.parentChild)
    ? generalInfo.parentChild
    : 'unknown'

  return (
    <>
      <div className="pt-2">
        <h4 className="text-base font-semibold text-[var(--text-primary)]">
          {corporateStructureGroup?.formTitle || 'Corporate Structure'}
        </h4>
      </div>

      <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Reseller Agreement */}
        <div>
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
            {resellerAgreementField?.formLabel || 'Reseller Agreement'}
          </label>
          <select
            className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
            value={generalInfo.resellerAgreement}
            onChange={(e) => {
              const value = e.target.value
              updateGeneralInfo('resellerAgreement', value as any)
              if (value !== 'yes') {
                if (generalInfo.resellerName !== '') updateGeneralInfo('resellerName', '')
                if (generalInfo.resellerRelationshipType !== 'unknown') updateGeneralInfo('resellerRelationshipType', 'unknown')
                if (generalInfo.resellerPredefinedConfig !== 'unknown') updateGeneralInfo('resellerPredefinedConfig', 'unknown')
              }
            }}
            disabled={disabled}
          >
            {(resellerAgreementField?.options || [
              { value: 'unknown', label: mapLabel("Don't Know") },
              { value: 'yes', label: 'Yes' },
              { value: 'no', label: 'No' },
            ]).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            <span className="text-primary font-bold mr-1">*</span>
            {resellerAgreementField?.formDescription || ''}
          </p>
        </div>

        {generalInfo.resellerAgreement === 'yes' && (
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
              {resellerNameField?.formLabel || 'Reseller'}
            </label>
            <input
              type="text"
              className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
              placeholder={resellerNameField?.placeholder || 'Enter reseller name'}
              value={generalInfo.resellerName}
              onChange={(e) => updateGeneralInfo('resellerName', e.target.value)}
              disabled={disabled}
            />
            <p className="text-xs text-[var(--text-muted)] mt-1">
              <span className="text-primary font-bold mr-1">*</span>
              {resellerNameField?.formDescription || ''}
            </p>
          </div>
        )}
      </div>

      {generalInfo.resellerAgreement === 'yes' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Reseller Relationship Type */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
              {resellerRelationshipTypeField?.formLabel || 'Reseller relationship'}
            </label>
            <select
              className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
              value={generalInfo.resellerRelationshipType}
              onChange={(e) => updateGeneralInfo('resellerRelationshipType', e.target.value as any)}
              disabled={disabled}
            >
              {(resellerRelationshipTypeField?.options || [
                { value: 'unknown', label: mapLabel("Don't Know") },
                { value: 'selling_to_customers', label: 'This scope is for the reseller selling to their customers' },
                { value: 'customer_of_reseller', label: 'This scope is for a customer of a reseller' },
              ]).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              {resellerRelationshipTypeField?.formDescription || ''}
            </p>
            {generalInfo.resellerRelationshipType === 'customer_of_reseller' && (
              <ResellerScopeLinker />
            )}
            {generalInfo.resellerRelationshipType === 'selling_to_customers' && (
              <ResellerChildrenList />
            )}
          </div>

          {/* Reseller Predefined Configuration */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
              {resellerPredefinedConfigField?.formLabel || 'Predefined configuration'}
            </label>
            <select
              className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
              value={generalInfo.resellerPredefinedConfig}
              onChange={(e) => updateGeneralInfo('resellerPredefinedConfig', e.target.value as any)}
              disabled={disabled || isResellerChild}
            >
              <option value="unknown">Don&apos;t Know</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              {isResellerChild ? (
                <span className="text-blue-400">Inherited from reseller parent — read-only.</span>
              ) : (
                resellerPredefinedConfigField?.formDescription || ''
              )}
            </p>
          </div>

          {/* Child Account Structure — only on reseller parent scopes */}
          {generalInfo.resellerRelationshipType === 'selling_to_customers' && (
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                Child account structure
              </label>
              <select
                className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                value={generalInfo.resellerChildAccountStructure}
                onChange={(e) => updateGeneralInfo('resellerChildAccountStructure', e.target.value as any)}
                disabled={disabled}
              >
                <option value="unknown">Don&apos;t Know</option>
                <option value="child_under_parent">Child accounts under this parent</option>
                <option value="own_parent_account">Children have their own parent accounts</option>
              </select>
              <p className="text-xs text-[var(--text-muted)] mt-1">
                Determines the Parent/Child account structure inherited by all reseller children linked to this scope.
              </p>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Parent / Child Relationship */}
        <div>
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
            {parentChildField?.formLabel || 'Parent / Child Relationship'}
          </label>
          <select
            className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
            value={parentChildValue}
            onChange={(e) => {
              console.log('parentChild onChange:', e.target.value)
              updateGeneralInfo('parentChild', e.target.value as any)
            }}
            disabled={disabled || isResellerChild}
          >
            {(parentChildField?.options || [
              { value: 'unknown', label: mapLabel("Don't Know") },
              { value: 'parent_only', label: 'Parent Only' },
              { value: 'parent_child', label: 'Parent + Child' },
              { value: 'child', label: 'Child Only' },
            ]).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            {isResellerChild ? (
              <span className="text-blue-400">Inherited from reseller parent — read-only.</span>
            ) : (
              <>
                <span className="text-primary font-bold mr-1">*</span>
                {parentChildField?.formDescription ||
                  'Required for completion. Parent Only = standalone parent account. Parent + Child = parent with nested child that can override settings. Child Only = child account under an existing parent (e.g. reseller children).'}
              </>
            )}
          </p>
        </div>

        {(parentChildValue === 'parent_only' || parentChildValue === 'parent_child' || parentChildValue === 'child') && (
          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Parent account fields — shown for parent_only, parent_child, and child (to identify the parent) */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                {parentAccountNameField?.formLabel || 'Parent Account Name'}
              </label>
              <input
                type="text"
                className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                placeholder={parentAccountNameField?.placeholder || 'Enter parent account name'}
                value={generalInfo.parentAccountName}
                onChange={(e) => updateGeneralInfo('parentAccountName', e.target.value)}
                disabled={disabled}
              />
              <p className="text-xs text-[var(--text-muted)] mt-1">
                {parentAccountNameField?.formDescription || 'Optional: FrankieOne account name for the parent.'}
              </p>
            </div>

            {parentChildValue !== 'child' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                    {parentUatIdField?.formLabel || 'Parent UAT ID'}
                  </label>
                  <input
                    type="text"
                    className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                    placeholder={parentUatIdField?.placeholder || 'Enter parent UAT ID'}
                    value={generalInfo.parentUatId}
                    onChange={(e) => updateGeneralInfo('parentUatId', e.target.value)}
                    disabled={disabled}
                  />
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    {parentUatIdField?.formDescription || 'Optional: UAT environment account identifier.'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                    {parentProdIdField?.formLabel || 'Parent Production ID'}
                  </label>
                  <input
                    type="text"
                    className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                    placeholder={parentProdIdField?.placeholder || 'Enter parent production ID'}
                    value={generalInfo.parentProdId}
                    onChange={(e) => updateGeneralInfo('parentProdId', e.target.value)}
                    disabled={disabled}
                  />
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    {parentProdIdField?.formDescription || 'Optional: Production environment account identifier.'}
                  </p>
                </div>
              </>
            )}

            {(parentChildValue === 'parent_child' || parentChildValue === 'child') && (
              <>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                    {childAccountNameField?.formLabel || 'Child Account Name'}
                  </label>
                  <input
                    type="text"
                    className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                    placeholder={childAccountNameField?.placeholder || 'Enter child account name'}
                    value={generalInfo.childAccountName}
                    onChange={(e) => updateGeneralInfo('childAccountName', e.target.value)}
                    disabled={disabled}
                  />
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    {childAccountNameField?.formDescription || 'Optional: FrankieOne account name for the child.'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                    {childUatIdField?.formLabel || 'Child UAT ID'}
                  </label>
                  <input
                    type="text"
                    className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                    placeholder={childUatIdField?.placeholder || 'Enter child UAT ID'}
                    value={generalInfo.childUatId}
                    onChange={(e) => updateGeneralInfo('childUatId', e.target.value)}
                    disabled={disabled}
                  />
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    {childUatIdField?.formDescription || 'Optional: UAT environment account identifier.'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                    {childProdIdField?.formLabel || 'Child Production ID'}
                  </label>
                  <input
                    type="text"
                    className="w-full h-12 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                    placeholder={childProdIdField?.placeholder || 'Enter child production ID'}
                    value={generalInfo.childProdId}
                    onChange={(e) => updateGeneralInfo('childProdId', e.target.value)}
                    disabled={disabled}
                  />
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    {childProdIdField?.formDescription || 'Optional: Production environment account identifier.'}
                  </p>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      </div>

      {/* Testing */}
      <div className="pt-2">
        <h4 className="text-base font-semibold text-[var(--text-primary)]">
          {testingGroup?.formTitle || 'Testing'}
        </h4>
      </div>

      <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
      <div>
        <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
          {testingRequirementsField?.formLabel || 'Test details'}
        </label>
        <textarea
          className="w-full min-h-28 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-y"
          placeholder={testingRequirementsField?.placeholder || 'Capture specific UAT test preferences'}
          value={generalInfo.testDetails}
          onChange={(e) => updateGeneralInfo('testDetails', e.target.value)}
          disabled={disabled}
        />
        <p className="text-xs text-[var(--text-muted)] mt-1">
          {testingRequirementsField?.formDescription ||
            testingGroup?.formDescription ||
            'In UAT FrankieOne does not typically collect live connectors. We can provide mock connectors to simulate different\n' +
              'cases. Please notify us if you want live or mock connectors in UAT. Alternatively your UAT environment uses random\n' +
              'connectors and responds to testbed entities'}
        </p>
      </div>
      </div>

      {/* Billing Requirements */}
      <div className="pt-2">
        <h4 className="text-base font-semibold text-[var(--text-primary)]">
          Billing Requirements
        </h4>
      </div>

      <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
      <div>
        <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
          {billingRequirementsField?.formLabel || 'Billing Requirements'}
        </label>
        <textarea
          className="w-full min-h-28 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-y"
          placeholder={billingRequirementsField?.placeholder || 'e.g., Specific department codes required for cost allocation, outsourced billing to third party, multi-entity billing split'}
          value={generalInfo.billingRequirements}
          onChange={(e) => updateGeneralInfo('billingRequirements', e.target.value)}
          disabled={disabled}
        />
        <p className="text-xs text-[var(--text-muted)] mt-1">
          {billingRequirementsField?.formDescription || 'Capture preferences related to billing, such as use of department codes, outsourcing arrangements, or other special requirements.'}
        </p>
      </div>
      </div>

      {/* SOW Variation */}
      <div className="pt-2">
        <h4 className="text-base font-semibold text-[var(--text-primary)]">
          {sowVariationGroup?.formTitle || 'SOW Variation'}
        </h4>
      </div>

      <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
      <div>
        <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
          {sowVariationNoteField?.formLabel || 'Variation note'}
        </label>
        <textarea
          className="w-full min-h-28 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-y"
          placeholder={sowVariationNoteField?.placeholder || 'Capture the agreed variation details'}
          value={generalInfo.sowVariationNote || ''}
          onChange={(e) => updateGeneralInfo('sowVariationNote', e.target.value)}
          disabled={disabled}
        />
        {sowVariationNoteField?.formDescription ? (
          <p className="text-xs text-[var(--text-muted)] mt-1">{sowVariationNoteField.formDescription}</p>
        ) : null}
      </div>
      </div>
    </>
  )
}

/** Small sub-component for linking a child scope to a parent reseller scope. */
function ResellerScopeLinker() {
  const [searchParams] = useSearchParams()
  const dealId = searchParams.get('session')
  const { resellerConstraints, parentCustomerName, setResellerConstraints } = useScopeStore()
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<Array<{ id: number; customer_name: string }>>([])
  const [searching, setSearching] = useState(false)
  const [linking, setLinking] = useState(false)

  const numericDealId = dealId ? parseInt(dealId, 10) : null

  const doSearch = useCallback(async () => {
    if (search.length < 2) { setResults([]); return }
    setSearching(true)
    try {
      const res = await dealsApi.searchResellerParents(search)
      if (res.success && res.data) {
        const deals = res.data.filter(d => d.id !== numericDealId)
        setResults(deals)
      }
    } finally {
      setSearching(false)
    }
  }, [search, numericDealId])

  useEffect(() => {
    const timer = setTimeout(doSearch, 300)
    return () => clearTimeout(timer)
  }, [doSearch])

  const handleLink = async (parentDealId: number) => {
    if (!numericDealId) return
    setLinking(true)
    try {
      // First we need the parent scope ID — fetch it via the parent deal's scope
      const parentScopeRes = await scopesApi.getByDealId(parentDealId)
      if (!parentScopeRes.success || !parentScopeRes.data) {
        showToast('Parent deal has no scope configured', 'error')
        return
      }
      const parentScopeData = parentScopeRes.data as unknown as Record<string, unknown>
      const parentScopeId = parentScopeData.id as number

      const res = await scopeVersionsApi.setParentScope(numericDealId, parentScopeId)
      if (res.success) {
        const constraints = deriveConstraints(parentScopeData)
        const pName = (parentScopeData.customer_name as string) || 'Reseller'
        setResellerConstraints(constraints, pName)

        // Import parent scope data into child — copy all config tabs + general technical setup
        const parentMapped = mapDbScopeToStoreState(parentScopeRes.data)
        const { updateGeneralInfo: setGeneralField } = useScopeStore.getState()

        // Import technical setup fields from parent general info
        setGeneralField('integrationType', parentMapped.generalInfo.integrationType)
        setGeneralField('platformVersion', parentMapped.generalInfo.platformVersion)
        setGeneralField('ssoAccess', parentMapped.generalInfo.ssoAccess)
        setGeneralField('aiFeatures', parentMapped.generalInfo.aiFeatures)

        // Import predefined config from parent
        setGeneralField('resellerPredefinedConfig', parentMapped.generalInfo.resellerPredefinedConfig)

        // Derive parentChild from parent's resellerChildAccountStructure
        const parentStructure = parentMapped.generalInfo.resellerChildAccountStructure
        if (parentStructure === 'child_under_parent') {
          setGeneralField('parentChild', 'child')
        } else if (parentStructure === 'own_parent_account') {
          setGeneralField('parentChild', 'parent_child')
        }

        // Import full config tabs from parent
        useScopeStore.setState((state) => ({
          scope: {
            ...state.scope,
            kycConfig: parentMapped.kycConfig,
            amlConfig: parentMapped.amlConfig,
            biometricsConfig: parentMapped.biometricsConfig,
            kybConfig: parentMapped.kybConfig,
            recipesConfig: parentMapped.recipesConfig,
            workflowsConfig: parentMapped.workflowsConfig,
            rulesConfig: parentMapped.rulesConfig,
            riskConfig: parentMapped.riskConfig,
          },
        }))

        setSearch('')
        setResults([])
        showToast(`Linked to reseller: ${pName} — scope imported and locked`, 'success')
      } else {
        showToast(res.error || 'Failed to link', 'error')
      }
    } finally {
      setLinking(false)
    }
  }

  const handleUnlink = async () => {
    if (!numericDealId) return
    if (!window.confirm('Unlinking will clear all inherited config (KYC, AML, IDV, KYB, Recipes, Fraud) and reset Technical Setup. Continue?')) return
    setLinking(true)
    try {
      const res = await scopeVersionsApi.setParentScope(numericDealId, null)
      if (res.success) {
        setResellerConstraints(null, null)

        // Reset imported sections back to defaults
        const { getDefaultKycConfig, getDefaultAmlConfig, getDefaultBiometricsConfig, getDefaultKybConfig, getDefaultRulesConfig, getDefaultRiskConfig } = await import('../../utils/defaults')
        useScopeStore.setState((state) => ({
          scope: {
            ...state.scope,
            kycConfig: getDefaultKycConfig(),
            amlConfig: getDefaultAmlConfig(),
            biometricsConfig: getDefaultBiometricsConfig(),
            kybConfig: getDefaultKybConfig(),
            recipesConfig: [],
            workflowsConfig: [],
            rulesConfig: getDefaultRulesConfig(),
            riskConfig: getDefaultRiskConfig(),
          },
        }))
        const { updateGeneralInfo: setGeneralField } = useScopeStore.getState()
        setGeneralField('integrationType', 'unknown')
        setGeneralField('platformVersion', 'unknown')
        setGeneralField('ssoAccess', 'unknown')
        setGeneralField('aiFeatures', 'unknown')
        setGeneralField('parentChild', 'unknown')
        setGeneralField('resellerPredefinedConfig', 'unknown')

        showToast('Reseller link removed — inherited config cleared', 'success')
      }
    } finally {
      setLinking(false)
    }
  }

  if (resellerConstraints && parentCustomerName) {
    return (
      <div className="mt-3 rounded-lg border border-blue-400/40 bg-blue-400/10 px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-blue-300">
          <Link2 size={14} />
          <span>Linked to reseller: <strong>{parentCustomerName}</strong></span>
          <span className="text-blue-400/60">· KYC, AML, IDV, KYB, Recipes, Fraud & Technical Setup imported and locked</span>
        </div>
        <button
          type="button"
          onClick={handleUnlink}
          disabled={linking}
          className="text-blue-400 hover:text-red-400 transition-colors disabled:opacity-50"
          title="Unlink reseller"
        >
          <X size={14} />
        </button>
      </div>
    )
  }

  return (
    <div className="mt-3 space-y-2">
      <div className="text-xs text-[var(--text-muted)]">
        Search for the parent reseller deal to link this scope to. Only deals configured as reseller parents will appear. The child scope will inherit constraints from the reseller.
      </div>
      <input
        type="text"
        className="w-full h-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary"
        placeholder="Search reseller deals..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {searching && <div className="text-xs text-[var(--text-muted)]">Searching...</div>}
      {results.length > 0 && (
        <div className="border border-[var(--border-color)] rounded-lg overflow-hidden max-h-48 overflow-y-auto">
          {results.map(deal => (
            <button
              key={deal.id}
              type="button"
              className="w-full px-3 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] border-b border-[var(--border-color)] last:border-0 disabled:opacity-50"
              onClick={() => handleLink(deal.id)}
              disabled={linking}
            >
              {deal.customer_name} <span className="text-[var(--text-muted)]">#{deal.id}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/** Shows child scopes linked to this reseller parent scope. */
function ResellerChildrenList() {
  const [searchParams] = useSearchParams()
  const dealId = searchParams.get('session')
  const numericDealId = dealId ? parseInt(dealId, 10) : null
  const [children, setChildren] = useState<Array<{ scope_id: number; deal_id: number; customer_name: string | null; hubspot_deal_name: string | null }>>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!numericDealId) return
    let cancelled = false
    setLoading(true)
    scopeVersionsApi.getChildScopes(numericDealId).then((res) => {
      if (!cancelled && res.success && res.data) {
        setChildren(res.data.children)
      }
      if (!cancelled) setLoading(false)
    })
    return () => { cancelled = true }
  }, [numericDealId])

  if (loading) return <div className="mt-3 text-xs text-[var(--text-muted)]">Loading children...</div>
  if (children.length === 0) return <div className="mt-3 text-xs text-[var(--text-muted)]">No child scopes linked to this reseller yet.</div>

  return (
    <div className="mt-3 space-y-1">
      <div className="text-xs text-[var(--text-muted)]">
        {children.length} child scope{children.length !== 1 ? 's' : ''} linked to this reseller:
      </div>
      <div className="border border-[var(--border-color)] rounded-lg overflow-hidden">
        {children.map((child) => (
          <a
            key={child.deal_id}
            href={`/scope?session=${child.deal_id}`}
            className="flex items-center justify-between px-3 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] border-b border-[var(--border-color)] last:border-0"
          >
            <span className="truncate">{child.customer_name || child.hubspot_deal_name || `Deal #${child.deal_id}`}</span>
            <span className="text-xs text-primary shrink-0 ml-2">Open →</span>
          </a>
        ))}
      </div>
    </div>
  )
}
